// Manager Sub-mode B guidance chat — DESIGN §5.6.2, §10.1.2 #2.
//
// Multi-turn conversation with Maeve about a specific case. Server is
// stateless; the transcript is POSTed in full on each turn and lives in
// `useManagerGuidanceChat`'s query-cache store, keyed by (caseId, threadId) —
// NOT in this component. History is still intentionally lost on refresh
// (Manager mode is a workplace advisory tool, not a persistent companion,
// which sidesteps the DO architecture question until M3 governance resolves
// it); what it now survives is an unmount. See that hook's header.
//
// Hidden by collapse rather than the case's `firstResponseSentAt` flag — the
// manager keeps using guidance throughout the case lifecycle (return-to-work,
// touch point prep, etc.) so this surface shouldn't auto-hide.

import { useEffect, useMemo } from 'react';
import type { GuidanceTurn } from '@besideu/shared/maeve';
import {
  useManagerGuidanceChat,
  explainGuidanceError,
  type GuidanceThreadId,
} from '@/lib/use-manager-guidance-chat.ts';
import { Button } from './ui/Button.tsx';
import { ChatBubble } from './chat/ChatBubble.tsx';
import { MaeveAvatar } from './brand/MaeveAvatar.tsx';

export function ManagerMaeveGuidance({
  caseId,
  threadId,
  title = 'Ask Maeve about this support pathway',
  subtitle = 'Policy questions, what-do-I-do-next, how-to-phrase — Maeve cites policy where she can. Anything Maeve says is advisory; the decision is always yours.',
  prefill,
  onPendingChange,
  onAnnounce,
  onQuestionCountChange,
}: {
  caseId: string;
  /**
   * Which conversation this card is — REQUIRED, see the CACHE KEY note in
   * `use-manager-guidance-chat.ts`. Two cards about the same case are two
   * different threads: the case-detail "ask about this case" card and the
   * check-in welcome-back drafter are doing different jobs, and sharing a
   * transcript would put one's turns into the other's CGC-audited payload.
   */
  threadId: GuidanceThreadId;
  // Journey 06: the check-in page reframes this as a welcome-back drafter for
  // the Pre_Return moment (title/subtitle + a seeded opener). The Manager mode
  // guidance endpoint already handles "how-to-phrase" — no new backend.
  title?: string;
  subtitle?: string;
  prefill?: string | undefined;
  /**
   * Reports `ask.isPending` to the caller, so a "change case" control can be
   * disabled while a request is in flight. Originally this prevented real
   * data loss: switching cases mid-request unmounted this component (via its
   * own `key={caseId}`) before the response landed, and the reply was
   * silently discarded into a stale closure. That hole is closed at the
   * source now — `useManagerGuidanceChat` appends inside its `mutationFn`
   * and writes to the query cache, both of which outlive the component — so
   * this is a clarity guard rather than a correctness one.
   */
  onPendingChange?: (pending: boolean) => void;
  /**
   * When provided, this card's own sr-only `aria-live` region is skipped and
   * Maeve's latest reply is reported here instead — a live region inside a
   * `hidden` ancestor (e.g. an inactive tab panel) never announces, silently
   * dropping the notification, so a caller that can be tabbed away from
   * (`ManagerCaseDetail`) lifts a single shared, always-visible live region
   * to the page level and feeds it from here.
   */
  onAnnounce?: (message: string) => void;
  /** Reports the question count so a caller (the "Ask Maeve" tab badge)
   * doesn't need its own copy of the turns→questions calculation or its own
   * subscription to the same guidance thread. */
  onQuestionCountChange?: (count: number) => void;
}) {
  const { history, ask, draft, setDraft, submit } = useManagerGuidanceChat(
    caseId,
    threadId,
    prefill,
  );

  // Deliberately omits `onPendingChange` from deps — callers rarely memoise
  // it, and re-firing this only when `ask.isPending` actually changes (not
  // on every parent render) is the intended behaviour.
  useEffect(() => {
    onPendingChange?.(ask.isPending);
  }, [ask.isPending]);

  // `history` only mutates in user+assistant pairs today; floor is a
  // defensive guard against future shapes (e.g. assistant turns appended
  // without a matching user turn).
  const questionCount = Math.floor(history.length / 2);

  // Deliberately omits `onQuestionCountChange` from deps — same rationale as
  // `onPendingChange` above.
  useEffect(() => {
    onQuestionCountChange?.(questionCount);
  }, [questionCount]);

  // Memoise the latest-assistant text so the `aria-live` region's children
  // are referentially stable across unrelated re-renders. See the matching
  // comment in `HrMaeveChat.tsx` for the full reasoning.
  const lastMaeveReplyForAt = useMemo(() => {
    const lastAssistant = [...history].reverse().find((t) => t.role === 'assistant');
    return lastAssistant ? `Maeve replied: ${lastAssistant.text}` : '';
  }, [history]);

  // Deliberately omits `onAnnounce` from deps — same rationale as
  // `onPendingChange` above.
  useEffect(() => {
    if (onAnnounce && lastMaeveReplyForAt) onAnnounce(lastMaeveReplyForAt);
  }, [lastMaeveReplyForAt]);

  return (
    <section className="space-y-4 rounded-lg border border-border p-6">
      <header className="flex items-start gap-3">
        {/* size=44, rings=false matches the established "avatar beside a
            heading" convention (ManagerFirstResponse.tsx, ManagerCheckIn.tsx),
            not an unexplained one-off. */}
        <MaeveAvatar size={44} rings={false} className="mt-0.5 shrink-0" />
        <div>
          <h2 className="text-base font-semibold">{title}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
        </div>
      </header>

      {history.length > 0 ? (
        <ChatTranscript history={history} />
      ) : prefill ? null : (
        // Only for the generic (no-prefill) entry point. Journey 06's
        // welcome-back drafter seeds the textarea via `prefill` — the manager
        // has effectively already started, so "here's what you could ask"
        // priming copy would sit above a draft that answers it already.
        <EmptyState />
      )}

      {/* Screen-reader-only live region carrying ONLY Maeve's latest reply.
          Wrapping the whole `<ol>` in `aria-live` would re-announce the
          manager's own turn alongside the response (their words are appended
          to the same list); a separate live region with just the assistant
          text avoids that echo. `polite` rather than `assertive` because a
          Maeve reply is not interruption-worthy — WCAG 2.1 AA 4.1.3.
          Pattern mirrors `HrMaeveChat`. Skipped when `onAnnounce` is
          provided — that caller hosts its own shared live region instead
          (see the prop doc above), and rendering both would double-announce
          when this card's tab/panel is the active one. */}
      {onAnnounce ? null : (
        <div aria-live="polite" className="sr-only">
          {lastMaeveReplyForAt}
        </div>
      )}

      {ask.error ? <GuidanceError error={ask.error} onRetry={() => ask.reset()} /> : null}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        className="space-y-2"
      >
        <label className="block">
          <span className="sr-only">Your question</span>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              // Cmd/Ctrl+Enter submits — common chat convention. Plain Enter
              // inserts a newline (managers often write multi-line questions).
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                e.preventDefault();
                submit();
              }
            }}
            placeholder="What does the policy say about extending leave?"
            // text-base = 16px to avoid iOS Safari zoom on focus.
            className="block w-full resize-y rounded-md border border-border bg-background p-3 text-base"
            rows={3}
            maxLength={4000}
            disabled={ask.isPending}
          />
        </label>
        <div className="flex flex-wrap items-center justify-between gap-2">
          {/* `role="status"` so AT announces transitions like
              "Press Cmd/Ctrl + Enter to ask." → "1 previous question in this
              session." — implicit `aria-live="polite"`, no interruption. The
              Maeve-reply live region above carries the response content; this
              one is the lightweight "your turn registered" confirmation.
              Pattern mirrors `HrMaeveChat`. */}
          <p role="status" className="text-xs text-muted-foreground">
            {questionCount === 0
              ? 'Press Cmd/Ctrl + Enter to ask.'
              : `${questionCount} previous ${questionCount === 1 ? 'question' : 'questions'} in this session.`}
          </p>
          <Button
            type="submit"
            loading={ask.isPending}
            disabled={draft.trim().length === 0 || ask.isPending}
            className="py-2 text-sm"
          >
            Ask Maeve
          </Button>
        </div>
      </form>
    </section>
  );
}

function EmptyState() {
  // Mirrors HrMaeveChat's empty state — priming copy so a manager who has
  // never used this surface sees it's an AI feature before they've typed
  // anything, rather than a bare textarea.
  return (
    <div className="rounded-md border border-dashed border-border bg-muted/40 p-4 text-sm text-muted-foreground">
      <p className="mb-2 font-medium text-foreground">A few things Maeve can help with:</p>
      <ul className="list-disc space-y-1 pl-5">
        <li>Bereavement policy questions — Maeve cites the policy section where she can.</li>
        <li>What to do next at this stage of the support pathway.</li>
        <li>How to phrase something you're not sure how to say.</li>
      </ul>
    </div>
  );
}

function ChatTranscript({ history }: { history: GuidanceTurn[] }) {
  return (
    <ol className="space-y-3" aria-label="Conversation with Maeve">
      {history.map((turn, idx) => (
        // History is append-only within this component lifetime, so index is a
        // stable key. (Resetting state remounts the list, which is fine.)
        <li key={idx}>
          <ChatBubble role={turn.role}>{turn.text}</ChatBubble>
        </li>
      ))}
    </ol>
  );
}

function GuidanceError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const { headline, detail } = explainGuidanceError(error);
  return (
    // `role="status"` (implicit `aria-live="polite"`) so screen readers
    // announce the failure when the card mounts — WCAG 2.1 AA 4.1.3 (Status
    // Messages). NOT `role="alert"` / `aria-live="assertive"`: a "couldn't
    // reach Maeve" network error is recoverable and low-stakes; the manager
    // has already stopped to wait for a response, so a polite announcement
    // won't be missed. Assertive is reserved for genuinely urgent events
    // (e.g. the Companion-mode crisis handoff in `RedFlagHandoff`).
    // Pattern mirrors `HrMaeveChat`.
    <div
      role="status"
      className="space-y-3 rounded-md border border-traffic-red/40 bg-traffic-red/5 p-4 text-sm"
    >
      <div>
        <p className="font-medium text-traffic-red">{headline}</p>
        <p className="mt-1 text-muted-foreground">{detail}</p>
      </div>
      <Button variant="secondary" onClick={onRetry} className="py-1.5 text-sm">
        Dismiss
      </Button>
    </div>
  );
}
